import React, { useMemo } from 'react'
import { baseUrl } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import {
  BillboardText,
  Button,
  colors,
  Divider,
  FlowGrid,
  H2,
  LayoutBand,
  Paragraph,
  Row,
  Separator,
  usePageWidth,
} from '@fs/zion-ui'
import { GenericHeading } from '@fs/zion-ui/dist/es/typography'
import { useTranslation } from 'react-i18next'

import videoLanguageMap from './videoLanguageMap'
import VideoPopup from '../../VideoPopup/VideoPopup'

function getStats(t) {
  return [
    {
      key: 'searchable-names',
      label: t('apac.nlihp.about.stats.searchable-names-label', 'Searchable Names in Historical Records'),
      value: t('apac.nlihp.about.stats.searchable-names', '13.2B'),
    },
    {
      key: 'digital-books',
      label: t('apac.nlihp.about.stats.digital-books-label', 'Digital Books Online'),
      value: t('apac.nlihp.about.stats.digital-books', '601K'),
    },
    {
      key: 'fs-centers',
      label: t('apac.nlihp.about.stats.fs-centers-label', 'FamilySearch Centers'),
      value: t('apac.nlihp.about.stats.fs-centers', '6,614'),
    },
    {
      key: 'digital-images',
      label: t('apac.nlihp.about.stats.digital-images-label', 'Digital Images Published'),
      value: t('apac.nlihp.about.stats.digital-images', '5.7B'),
    },
    {
      key: 'monthly-visits',
      label: t('apac.nlihp.about.stats.monthly-visits-label', 'Monthly Visits'),
      value: t('apac.nlihp.about.stats.monthly-visits', '32.5M'),
    },
    {
      key: 'tree-people',
      label: t('apac.nlihp.about.stats.tree-people-label', 'People in the Family Tree'),
      value: t('apac.nlihp.about.stats.tree-people', '1.7B'),
    },
  ]
}

function Stat({ label, value, color }) {
  const isMobile = usePageWidth()({ default: true, md: false })
  const isEnglish = i18n.language.startsWith('en')
  const englishHeadingSize = isMobile ? 'H1' : 'H2'
  // to account for longer text in other languages
  const nonEnglishHeadingSize = isMobile ? 'H2' : 'H3'
  const headingSize = isEnglish ? englishHeadingSize : nonEnglishHeadingSize

  return (
    <div>
      <BillboardText primaryColor={color}>
        <GenericHeading as="span" size={headingSize} supplementary>
          {value}
        </GenericHeading>
      </BillboardText>
      <Divider marginY="xxs" />
      <Paragraph balanced size="sm">
        {label}
      </Paragraph>
    </div>
  )
}
function VideoButton({ onClick }) {
  const { t } = useTranslation()
  const isMobile = usePageWidth()({ default: true, md: false })

  return (
    <Button data-testid="watch-video-button" fullWidth={isMobile} color="gray" emphasis="medium" onClick={onClick}>
      {t('apac.nlihp.about.watch-video', 'Watch Video')}
    </Button>
  )
}

function RowButtonWrapper({ children }) {
  const fullWidth = usePageWidth()({ default: true, md: false })
  return <div style={{ width: fullWidth ? '100%' : 'auto' }}>{children}</div>
}

export default function AboutFSSection({ color }) {
  const { t } = useTranslation()
  const atWidth = usePageWidth()
  const isMobile = atWidth({ default: true, xl: false })
  const fullWidth = atWidth({ default: true, md: false })
  const columnCount = atWidth({ default: 1, sm: 2, xl: 3 })
  const videoSrc = videoLanguageMap[i18n.language] || videoLanguageMap.en

  const stats = useMemo(() => getStats(t), [t])

  return (
    <LayoutBand color={colors.gray02} maxWidth="sm" marginY="xl">
      <H2 size={isMobile ? 'H2' : 'H3'} centered supplementary>
        {t('apac.nlihp.about.heading', 'About FamilySearch')}
      </H2>
      <Separator size="xs" />
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        <Paragraph centered balanced size="sm">
          {t(
            'apac.nlihp.about.subheading',
            'FamilySearch has been connecting families across generations for over 100 years.'
          )}
        </Paragraph>
      </div>
      <Separator size="lg" />
      <FlowGrid columnCount={columnCount} guttersX={isMobile ? 'lg' : 'xxl'} guttersY="sm">
        {stats.map((stat) => (
          <Stat key={stat.key} label={stat.label} value={stat.value} color={color} />
        ))}
      </FlowGrid>
      <Separator size="lg" />
      <Row alignX="center" wrap ChildWrapper={RowButtonWrapper}>
        <Button
          fullWidth={fullWidth}
          color="gray"
          emphasis="high"
          to={`${baseUrl}/about`}
          external
          linkName="button: about-familysearch: click"
          data-testid="about-learn-more-button"
        >
          {t('common-ui:learn-more.action')}
        </Button>
        <VideoPopup
          CustomButton={VideoButton}
          title={t('apac.nlihp.about.who-is-fs', 'Who is FamilySearch?')}
          url={videoSrc}
        />
      </Row>
    </LayoutBand>
  )
}
